import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './DynamicSizingGuide.module.scss';
import { type BreedWeight } from './BreedDataset';
import { SizeOption } from '../../../../types';
import { WeightUnit } from '../../../../lib/size';

interface DynamicSizingGuideResultProps {
  sizingGuideItem: BreedWeight;
  breedName?: string;
  isPuppy: boolean;
  currentWeight: number;
  weightUnit: WeightUnit;
  onSelect: (newValue: SizeOption) => void;
}

const getSizeString = (size: string, t: any) => {
  if (size === 'XL') {
    return t('x_large');
  } else if (size === 'L') {
    return t('large');
  } else if (size === 'M') {
    return t('medium');
  } else if (size === 'S') {
    return t('small');
  } else if (size === 'XS') {
    return t('x_small');
  } else {
    return t('unknown');
  }
};

const getSizeOption = (size: string): SizeOption | null => {
  if (size === 'XL') {
    return SizeOption.XLarge;
  } else if (size === 'L') {
    return SizeOption.Large;
  } else if (size === 'M') {
    return SizeOption.Medium;
  } else if (size === 'S') {
    return SizeOption.Small;
  } else if (size === 'XS') {
    return SizeOption.XSmall;
  } else {
    return null;
  }
};

type SizeKey = 'XS' | 'S' | 'M' | 'L' | 'XL';

export default function DynamicSizingGuideResult({
  sizingGuideItem,
  breedName,
  isPuppy,
  currentWeight,
  weightUnit,
  onSelect,
}: DynamicSizingGuideResultProps) {
  const { t } = useTranslation();
  const sizes: SizeKey[] = ['XS', 'S', 'M', 'L', 'XL'];
  const sortedSizes = sizes
    .map((size) => ({ size, percentage: sizingGuideItem[size] }))
    .sort((a, b) => {
      const percentageA = Number(a.percentage) || 0;
      const percentageB = Number(b.percentage) || 0;
      return percentageB - percentageA;
    });
  const [largest, secondLargest] = sortedSizes;
  const otherSizesPercentage = 100 - Math.round(largest.percentage) - Math.round(secondLargest.percentage);
  const sizeOption = getSizeOption(largest.size);

  return (
    <>
      <div className={styles.sizingGuideResultContainer}>
        {breedName && !isPuppy && (
          <div className={styles.resultHeader}>
            <Trans
              i18nKey="percentage_of_similar_dogs_wear_size_size"
              values={{ percentage: Math.round(largest.percentage), size: getSizeString(largest.size, t) }}
              components={{ size: <span className={styles.resultHeaderSize} /> }}
            />
          </div>
        )}
        {!breedName && !isPuppy && (
          <div className={styles.resultHeader}>
            <Trans
              i18nKey="percentage_of_similar_sized_dogs_wear_sizes_size"
              values={{ percentage: Math.round(largest.percentage), size: getSizeString(largest.size, t) }}
              components={{ size: <span className={styles.resultHeaderSize} /> }}
            />
          </div>
        )}
        {breedName && !isPuppy && (
          <div className={styles.currentBreedAndWeightText}>
            <Trans
              i18nKey={
                weightUnit === 'kg' ? 'collar_sizes_of_weight_kg_breed_on_fi' : 'collar_sizes_of_weight_lb_breed_on_fi'
              }
              values={{
                weight: currentWeight,
                breed: breedName,
              }}
              components={{
                weight: <span className={styles.currentBreedAndWeightBoldText} />,
                breed: <span className={styles.currentBreedAndWeightBoldText} />,
              }}
            />
          </div>
        )}
        {!breedName && !isPuppy && (
          <div className={styles.currentBreedAndWeightText}>
            <Trans
              i18nKey={
                weightUnit === 'kg' ? 'collar_sizes_of_weight_kg_dogs_on_fi' : 'collar_sizes_of_weight_lb_dogs_on_fi'
              }
              values={{ weight: currentWeight }}
              components={{ weight: <span className={styles.currentBreedAndWeightBoldText} /> }}
            />
          </div>
        )}
        {!isPuppy && (
          <div className={styles.percentageResultContainer}>
            <div className={styles.percentageResultItem}>
              <div>{getSizeString(largest.size, t)}</div>
              <div>
                {Math.round(largest.percentage)}
                {'%'}
              </div>
            </div>
            <div className={styles.percentageResultItem}>
              <div>{getSizeString(secondLargest.size, t)}</div>
              <div>
                {Math.round(secondLargest.percentage)}
                {'%'}
              </div>
            </div>
            <div className={styles.percentageResultItem}>
              <div>{t('other')}</div>
              <div>
                {Math.round(otherSizesPercentage)}
                {'%'}
              </div>
            </div>
          </div>
        )}
        {isPuppy && (
          <div className={styles.puppyRecommendationText}>
            {t('we_recommend_size', { size: getSizeString(largest.size, t) })}
          </div>
        )}
        <button className={styles.selectSizeButton} onClick={() => sizeOption && onSelect(sizeOption)}>
          {t('select_size', { size: getSizeString(largest.size, t) })}
        </button>
        {!isPuppy && (
          <div className={styles.stillNotSureText}>
            <span className={styles.stillNotSureBoldText}>{t('still_not_sure')}</span>{' '}
            {t('well_replace_your_collar_band_for_free_if_its_not_right')}
          </div>
        )}
      </div>
      {isPuppy && (
        <div className={styles.puppySpecialOfferSection}>
          <div className={styles.puppySpecialOfferTitle}>
            <div className={styles.pawIcon} />
            {t('fi_puppy_warranty')}
          </div>
          <div className={styles.puppySpecialOfferDescription}>
            <Trans
              i18nKey="fi_series_3_plus_is_the_smallest_lightest_wearable_for_your_pup_get_a_size_collar_now_and_well_swap_it_out_for_free_when_they_are_fully_grown"
              values={{
                size: getSizeString(largest.size, t).toLowerCase(),
              }}
              components={{
                sizeSpan: <span />,
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
